/** The table after each move of the game. Empty for a log without a start. */
export function replayFrames(log) {
    const seats = new Map([...log.roster]
        .sort((a, b) => a.seat - b.seat)
        .map((p) => [p.id, { id: p.id, name: p.name, card: null, handCount: p.hand.length, dice: [], state: 'in' }]));
    const name = (id) => (id == null ? '' : (seats.get(id)?.name ?? ''));
    const names = (ids) => ids.map(name).join(', ');
    const cardName = (id) => (id == null ? '' : (log.cards[id] ?? ''));
    const seat = (id) => seats.get(id);
    const frames = [];
    let warIds = [];
    let started = false;
    const push = (i, e, caption, actorId = null, victimIds = []) => {
        frames.push({
            event: i,
            round: e.round,
            at: e.at,
            caption,
            seats: [...seats.values()].map((s) => ({ ...s, dice: [...s.dice] })),
            actorId,
            victimIds,
            warIds: [...warIds],
        });
    };
    /** A card leaves the table: killed or lost in a war (out follows if the hand is empty). */
    const lose = (id) => {
        const s = seat(id);
        if (!s)
            return;
        s.card = null;
        s.state = 'killed';
    };
    log.events.forEach((e, i) => {
        if (!started && e.type !== 'start')
            return;
        switch (e.type) {
            case 'start':
                started = true;
                return push(i, e, { key: 'replay.start', vars: { n: seats.size } });
            case 'choose': {
                const s = seat(e.player);
                if (s) {
                    s.card = e.card;
                    s.handCount = Math.max(0, s.handCount - 1);
                    s.state = 'in';
                    s.dice = [];
                }
                // Cards chosen one after another make one frame.
                const last = frames.at(-1);
                if (last && log.events[last.event]?.type === 'choose')
                    frames.pop();
                return push(i, e, { key: 'replay.chosen', vars: {} });
            }
            case 'war':
                warIds = [...e.players];
                for (const id of warIds)
                    seat(id).dice = [];
                return push(i, e, { key: 'replay.war', vars: { names: names(e.players), card: cardName(e.card) } });
            case 'warRoll': {
                const s = seat(e.player);
                if (s)
                    s.dice = [...e.dice];
                const total = e.dice.reduce((a, b) => a + b, 0);
                return push(i, e, { key: 'replay.warRoll', vars: { name: name(e.player), total } }, e.player);
            }
            case 'warResult': {
                for (const id of e.losers)
                    lose(id);
                const caption = e.winners.length === 1
                    ? { key: 'replay.warWon', vars: { name: name(e.winners[0]) } }
                    : { key: 'replay.warTie', vars: { names: names(e.winners) } };
                push(i, e, caption, e.winners.length === 1 ? e.winners[0] : null, [...e.losers]);
                warIds = e.winners.length > 1 ? [...e.winners] : [];
                if (e.winners.length > 1)
                    for (const id of e.winners)
                        seat(id).dice = [];
                return;
            }
            case 'round':
                warIds = [];
                for (const s of seats.values())
                    s.dice = [];
                return push(i, e, { key: 'replay.round', vars: { round: e.round, name: name(e.dealer) } }, e.dealer);
            case 'roll': {
                const s = seat(e.player);
                if (s)
                    s.dice = [...e.kept, ...e.rolled];
                return push(i, e, { key: 'replay.roll', vars: { name: name(e.player) } }, e.player);
            }
            case 'endTurn': {
                const s = seat(e.player);
                if (s)
                    s.dice = [...e.held];
                if (e.endRound)
                    return push(i, e, { key: 'replay.endRound', vars: { name: name(e.player) } }, e.player);
                return push(i, e, { key: 'replay.endTurn', n: e.held.length, vars: { name: name(e.player) } }, e.player);
            }
            case 'attack': {
                const s = seat(e.player);
                if (s)
                    s.dice = [...e.dice];
                for (const id of e.killed)
                    lose(id);
                if (s && e.after === 'returned' && s.card !== null) {
                    s.card = null;
                    s.handCount += 1;
                    s.state = 'safe';
                }
                if (e.killed.length === 0)
                    return push(i, e, { key: 'replay.miss', vars: { name: name(e.player) } }, e.player);
                return push(i, e, { key: 'replay.attack', vars: { name: name(e.player), victims: names(e.killed) } }, e.player, [...e.killed]);
            }
            case 'wild': {
                const s = seat(e.player);
                if (s)
                    s.dice = [...e.dice];
                return push(i, e, { key: 'replay.wild', vars: { name: name(e.player) } }, e.player);
            }
            case 'out': {
                const s = seat(e.player);
                if (s) {
                    s.state = 'out';
                    s.handCount = 0;
                }
                return push(i, e, { key: 'replay.out', vars: { name: name(e.player) } }, null, [e.player]);
            }
            case 'ghostRoll': {
                const s = seat(e.player);
                if (s) {
                    s.dice = [...e.dice];
                    if (e.card !== null) {
                        s.handCount += 1;
                        s.state = 'in';
                    }
                }
                const caption = e.card !== null
                    ? { key: 'replay.ghostHit', vars: { name: name(e.player), card: cardName(e.card) } }
                    : { key: 'replay.ghostMiss', vars: { name: name(e.player) } };
                return push(i, e, caption, e.player);
            }
            case 'forfeit': {
                const s = seat(e.player);
                if (s) {
                    s.card = null;
                    s.handCount = 0;
                    s.state = 'out';
                }
                warIds = warIds.filter((id) => id !== e.player);
                return push(i, e, { key: 'replay.forfeit', vars: { name: name(e.player) } }, e.player);
            }
            case 'finish': {
                warIds = [];
                const w = e.winner === null ? undefined : seat(e.winner);
                if (w)
                    w.state = 'won';
                return push(i, e, w ? { key: 'replay.won', vars: { name: w.name } } : { key: 'replay.noWinner', vars: {} }, e.winner);
            }
            case 'deal': {
                const vars = { name: name(e.player), partner: name(e.partner), target: name(e.target) };
                const key = e.timeout ? 'replay.deal.timeout' : `replay.deal.${e.action}`;
                return push(i, e, { key, vars }, e.player, e.action === 'break' ? [e.partner] : []);
            }
            // 'turn' shows with its roll; bot reasoning and spectators aren't part of the table.
            case 'turn':
            case 'reason':
            case 'spectator':
                return;
        }
    });
    return frames;
}
/** Highlights listed at most. */
const MAX_HIGHLIGHTS = 6;
/** The game's MVP and highlights (first blood, the biggest attack, wars, five of a kinds, ghost comebacks, broken deals, the final blow). */
export function recapOf(log) {
    const players = [...log.stats.players].sort((a, b) => a.placement - b.placement);
    const name = (id) => log.roster.find((p) => p.id === id)?.name ?? '';
    const winner = log.events.findLast((e) => e.type === 'finish')?.winner ?? null;
    const top = [...players].sort((a, b) => b.kills - a.kills || a.placement - b.placement)[0];
    const mvpId = top && top.kills > 0 ? top.id : winner;
    const attacks = log.events.filter((e) => e.type === 'attack' && e.killed.length > 0);
    const highlights = [];
    const first = attacks[0];
    if (first) {
        highlights.push({
            key: 'recap.firstBlood',
            vars: { name: name(first.player), victim: name(first.killed[0]), round: first.round },
        });
    }
    const biggest = attacks.reduce((best, a) => (a.killed.length > (best?.killed.length ?? 1) ? a : best), undefined);
    if (biggest) {
        highlights.push({
            key: 'recap.biggestAttack',
            vars: { name: name(biggest.player), n: biggest.killed.length, round: biggest.round },
        });
    }
    const wars = log.events.filter((e) => e.type === 'war').length;
    if (wars > 0) {
        const warWinner = [...players].sort((a, b) => b.warsWon - a.warsWon)[0];
        highlights.push(warWinner && warWinner.warsWon > 0
            ? { key: 'recap.wars', n: wars, vars: { name: warWinner.name, won: warWinner.warsWon } }
            : { key: 'recap.warsNoWinner', n: wars, vars: {} });
    }
    for (const e of log.events) {
        if (e.type === 'wild')
            highlights.push({ key: 'recap.fiveOfAKind', vars: { name: name(e.player), round: e.round } });
        if (e.type === 'ghostRoll' && e.card !== null)
            highlights.push({ key: 'recap.ghostReturn', vars: { name: name(e.player), round: e.round } });
        if (e.type === 'deal' && e.action === 'break')
            highlights.push({
                key: 'recap.betrayal',
                vars: { name: name(e.player), victim: name(e.partner), round: e.round },
            });
    }
    const last = attacks.at(-1);
    if (last && winner !== null && last.player === winner && last !== first) {
        highlights.push({
            key: 'recap.finalBlow',
            vars: { name: name(last.player), victims: last.killed.map(name).join(', ') },
        });
    }
    // Keep the final blow (last) when cutting the list down.
    const cut = highlights.length > MAX_HIGHLIGHTS ? [...highlights.slice(0, MAX_HIGHLIGHTS - 1), highlights.at(-1)] : highlights;
    return { mvpId, highlights: cut, players };
}
//# sourceMappingURL=replay.js.map